<template>
  <div>
    <d-row no-gutters class="page-header py-4 mb-2">
      <d-col col sm="6" class="text-center text-sm-left mb-4 mb-sm-0">
        <template v-if="isInPopup">
          <h4>Lista uprawnień</h4>
        </template>
        <template v-else>
          <h4>Lista upoważnień pacjenta:
            <p class="mt-1 mb-0 patient-name">
              {{ patient.name + ' ' + patient.last_name }}
            </p>
          </h4>
        </template>
      </d-col>
      <d-col col sm="6" class="text-right text-sm-right d-flex justify-content-end align-items-end">
        <d-button
          theme="success"
          @click.prevent="showAddAuthPopup"
          :disabled="!loaded">
          <i class="material-icons">add</i>
          Dodaj upoważnienie
        </d-button>
      </d-col>
    </d-row>

    <d-row class="d-flex ml-0 mb-3 page-filters w-100">
      <validation-observer
        ref="authorizations-filters-form"
        tag="form"
        class="d-flex w-100">
        <d-input
          placeholder="Podaj imię, nazwisko lub telefon"
          v-model="filters.search" />

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">Szukaj</d-button>
        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">Resetuj filtry</d-button>
      </validation-observer>
    </d-row>

    <table class="table mt-0 mb-0 full-width">
      <thead class="bg-light">
        <tr>
          <th scope="col" class="border-0">Id</th>
          <th scope="col" class="border-0">Imię i nazwisko</th>
          <th scope="col" class="border-0">Telefon</th>
          <th scope="col" class="border-0">Zgody</th>
          <th scope="col" class="border-0 text-center">Plik do pobrania</th>
          <th scope="col" class="border-0 text-center">Akcje</th>
        </tr>
      </thead>
      <tbody v-if="loaded">
        <tr
          v-for="(authorization, authorizationIndex) of authorizations"
          :key="'authorization-' + authorizationIndex">
          <td class="align-middle">
            {{ authorization.id }}
          </td>
          <td class="align-middle">
            {{ authorization.name }} {{ authorization.last_name }}
          </td>
          <td class="align-middle">
            {{ authorization.phone }}
          </td>
          <td class="align-middle">
            <div class="healthAndMedical">
              <p>
                Uzyskiwanie informacji o zdrowiu i świadczeniach:
                <span :style="authorization.health_benefits_inf === '1' ? 'color: #17c671' : 'color: #c4183c'">
                  {{ authorization.health_benefits_inf === '1' ? 'Tak' : 'Nie' }}
                </span>
              </p>
              <p>
                Uzyskiwanie dokumentacji medycznej:
                <span :style="authorization.medical_records === '1' ? 'color: #17c671' : 'color: #c4183c'">
                  {{ authorization.medical_records === '1' ? 'Tak' : 'Nie'}}
                </span>
              </p>
            </div>
          </td>
          <td class="align-middle text-center">
            <template v-if="authorization.scan_file.length">
              <d-button
                theme="info"
                class="mr-2"
                outline
                :disabled="actionInProgress !== false"
                @click.prevent="downloadFile(authorization.id, authorization.scan_file)">
                <template v-if="actionInProgress !== authorization.scan_file">
                  <i class="material-icons">cloud_download</i><span class="ml-2">Pobierz</span>
                </template>
                <template v-else>
                  <div class="spinner-border spinner-border-sm" role="status"></div>
                </template>
              </d-button>
            </template>
            <template v-else>
              <span style="opacity: 0.5">Brak</span>
            </template>
          </td>
          <td
            class="align-middle text-center"
            style="width: 130px;">
            <d-button
              theme="primary"
              @click.prevent="showEditAuthPopup(authorization)"
              class="mr-2"
              title="Edytuj">
              <i class="material-icons">edit</i>
            </d-button>
            <d-button
              theme="danger"
              :disabled="removeInProgress"
              @click.prevent="removeItem(authorization.id)"
              title="Usuń">
              <i class="material-icons">delete</i>
            </d-button>
          </td>
        </tr>
        <tr v-if="authorizations.length === 0">
          <td
            colspan="6"
            class="text-center">
            Brak upoważnień do wyświetlenia&hellip;
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="!loaded" class="pb-3 pt-3 text-center">
      Trwa ładowanie danych&hellip;
    </div>

    <d-alert
      v-if="loadError"
      show
      theme="warning">
      Wczytywanie danych nie powiodło się. <a href="javascript:window.location.reload();" class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
    </d-alert>

    <patients-form-auth-popup
      :authorization="editAuthorization"
      :is-visible="isShowAuthPopup"
      :on-close-popup="handleCloseAuthPopup"
      :on-submit-callback="debouncedLoadFilteredData"
      :patient-id="patient.id" />
  </div>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import api from '@/api';
import debounce from 'lodash.debounce';
import ListUtils from '@/utils/ListUtils.js';
import FormUtils from '@/utils/FormUtils.js';
import PatientsFormAuthPopup from '@/components/forms/patients/PatientsFormAuthPopup';

export default {
  name: 'patients-form-auth',
  props: {
    isInPopup: {
      default: false,
      type: Boolean
    },
    isVisible: {
      default: false,
      type: Boolean
    },
    patient: {
      required: true,
      type: Object
    }
  },
  components: {
    'patients-form-auth-popup': PatientsFormAuthPopup
  },
  watch: {
    'isVisible': function (newValue) {
      if (newValue && !this.loaded) {
        this.debouncedLoadFilteredData();
      }
    }
  },
  data () {
    return {
      actionInProgress: false,
      authorizations: [],
      editAuthorization: null,
      filters: {
        search: ''
      },
      isShowAuthPopup: false,
      loaded: false,
      loadError: false,
      removeInProgress: false
    }
  },
  mounted () {
    if (this.isVisible) {
      this.loadItemsData();
    }
  },
  methods: {
    loadItemsData () {
      let where = {
        search: this.filters.search
      };

      ListUtils.loadItemsData(this, {
        endpoint: '/patients/auth/items/' + this.patient.id,
        listField: 'authorizations',
        params: {
          where
        }
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      Vue.set(this, 'loaded', false);
      Vue.set(this, 'loadError', false);
      this.loadItemsData();
    }, 250),
    validateFiltersForm () {
      FormUtils.validate(this.$refs['medical-examinations-filters-form'], this.debouncedLoadFilteredData);
    },
    clearFilters () {
      this.filters = {
        search: ''
      };
      this.debouncedLoadFilteredData();
    },
    removeItem (authorizationID) {
      this.removeInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/patients/auth/delete/',
        id: authorizationID,
        successTitle: 'Usunięto upoważnienie',
        successText: 'Wybrane upoważnienie został usunięty',
        confirmTitle: 'Czy na pewno chcesz usunąć to upoważnienie?',
        confirmText: 'Tej operacji nie można cofnąć',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie upoważnienia nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.removeInProgress = false;
          this.debouncedLoadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    downloadFile (authenticatiobnID, name) {
      this.actionInProgress = true;
      let filename = name;

      axios({
        url: `${api}patients/auth/get-file/` + authenticatiobnID,
        method: 'POST',
        responseType: 'blob'
      }).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.addEventListener('click', this.clickCallback, false);
        link.click();
        this.actionInProgress = false;
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: 'Wystąpił błąd podczas pobierania',
          html: 'Spróbuj ponownie',
          type: 'warning',
          confirmButtonText: 'OK',
        });
        this.actionInProgress = false;
      });
    },
    clickCallback (e) {
      e.stopPropagation();
    },
    showEditAuthPopup (authorization) {
      Vue.set(this, 'editAuthorization', authorization);
      Vue.set(this, 'isShowAuthPopup', true);
    },
    showAddAuthPopup () {
      Vue.set(this, 'editAuthorization', null);
      Vue.set(this, 'isShowAuthPopup', true);
    },
    handleCloseAuthPopup () {
      Vue.set(this, 'isShowAuthPopup', false);
      Vue.set(this, 'editAuthorization', null);
    }
  }
}
</script>
